// =============================================================================
// mandatsUtils.js — Historisation des périodes de mandat (CA / AG / Bureau / CODIR)
// =============================================================================
// Un délégué peut être nommé, quitter son mandat, puis le REPRENDRE. Avant, la
// fiche ne portait qu'UN objet mandat par instance (delegates.mandatCA…),
// écrasé à chaque nomination : la période précédente (debut/fin/motifSortie/
// dateSortie) était détruite. Désormais toute période CLOSE est archivée dans
// delegates.mandatsHistorique (append-only) au moment où le mandat est rouvert
// ou effacé — la fiche garde ainsi la chronologie complète des allers-retours.
//
// Convention de statut UNIFIÉE : « actif » = en exercice ; « sorti » = sortie
// de mandat ; « termine » = compte soft-supprimé. Historiquement la moitié des
// tests faisaient `statut !== "sorti"` et comptaient un mandat « termine »
// comme actif (délégué supprimé encore votant) — passer par mandatActif().
// =============================================================================

export const CHAMPS_MANDAT = ["mandatCA", "mandatAG", "mandatBureau", "mandatCODIR"];

// ─── Dates de mandat AU JOUR ─────────────────────────────────────────────────
// Demande Pierre 24/08/2026 : début et fin de mandat précis au JOUR. Le stock
// est MIXTE : l'UI historique écrivait « YYYY-MM » (inputs type=month), le
// moteur d'effets statutaires (cooptations) écrit « YYYY-MM-DD » depuis
// toujours. Ces helpers sont LE point de passage : trois parseurs supposaient
// « YYYY-MM » (new Date(fin+"-01")) et rendaient Invalid Date sur une date au
// jour — un mandat échu n'était alors jamais filtré, silencieusement.
//   • debutEnJour("2024-07")    → "2024-07-01"  (début de mois)
//   • finEnJour("2028-07")      → "2028-07-31"  (fin de mois, INCLUSIVE : un
//     mandat « jusqu'à juillet » court jusqu'au 31 juillet)
//   • une valeur déjà au jour passe inchangée ; illisible → "".
export function debutEnJour(v) {
  const s = String(v || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (/^\d{4}-\d{2}$/.test(s)) return `${s}-01`;
  return "";
}
export function finEnJour(v) {
  const s = String(v || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (/^\d{4}-\d{2}$/.test(s)) {
    const [y, mo] = s.split("-").map(Number);
    return `${s}-${String(new Date(y, mo, 0).getDate()).padStart(2, "0")}`;
  }
  return "";
}
// La fin de mandat est-elle DÉPASSÉE (strictement : le jour de fin reste en
// exercice) ? Vide ou illisible → false (pas d'échéance connue).
export function finDepassee(v, ref = new Date()) {
  const j = finEnJour(v);
  if (!j) return false;
  const refJour = `${ref.getFullYear()}-${String(ref.getMonth() + 1).padStart(2, "0")}-${String(ref.getDate()).padStart(2, "0")}`;
  return j < refJour;
}

// DOCTRINE (statu quo assumé, à re-trancher avec Pierre si besoin) : une fin
// de mandat DÉPASSÉE ne retire PAS automatiquement le droit de vote/quorum —
// seule la sortie explicite (statut "sorti", acte de gouvernance) fait foi.
// Les dates échues sont SIGNALÉES (badges « échu » Gouvernance, listes auto)
// pour être régularisées, jamais appliquées en silence à un décompte de voix.
export const DATES_PILOTENT_EXERCICE = false;

// Un objet mandat est-il en exercice ?
export const mandatActif = (m) => !!m && m.statut !== "sorti" && m.statut !== "termine";

// Un mandat OUVRE-t-il le droit (vote/quorum) ? Tolérance legacy : beaucoup de
// fiches portent le rôle sans objet mandat (détectées par le scan super-admin
// « rôle sans mandat ») — l'absence d'objet ne retire pas le droit, seule une
// sortie explicite le retire.
export const mandatOuvreDroit = (m) => !m || mandatActif(m);

// Périodes archivées d'une fiche : [{champ, debut, fin, statut, motifSortie,
// dateSortie, archiveLe}] — filtrable par champ ("mandatCA"…).
export const historiqueMandats = (d, champ = null) =>
  (Array.isArray(d?.mandatsHistorique) ? d.mandatsHistorique : []).filter((h) => !champ || h.champ === champ);

// Règle Pierre 26/08/2026 : une période close pour « Correction d'erreur »
// (retrait d'une nomination erronée) est un LOG technique, pas un fait de
// gouvernance — elle reste stockée dans mandatsHistorique mais ne doit JAMAIS
// apparaître sur une fiche. Tous les AFFICHAGES d'historique passent par ce
// filtre ; estCorrection couvre aussi le mandat courant clos pour ce motif.
export const MOTIF_CORRECTION = "Correction d'erreur";
export const estCorrection = (m) => !!m && m.motifSortie === MOTIF_CORRECTION;
export const historiqueVisible = (d, champ = null) =>
  historiqueMandats(d, champ).filter((h) => !estCorrection(h));

// Archive la période portée par l'objet mandat courant SI elle est close, et
// renvoie le patch ({mandatsHistorique} ou {}) à fusionner dans la fiche.
// À appeler AVANT d'écraser le mandat (reprise) ou de l'effacer.
export function archiverMandat(d, champ) {
  const m = d?.[champ];
  if (!m || typeof m !== "object" || mandatActif(m)) return {};
  return {
    mandatsHistorique: [...historiqueMandats(d), {
      champ, debut: m.debut || "", fin: m.fin || "", statut: m.statut || "sorti",
      motifSortie: m.motifSortie || null, dateSortie: m.dateSortie || null,
      archiveLe: new Date().toISOString().slice(0, 10),
    }],
  };
}

// Ouvre une période de mandat PROPRE (première nomination ou reprise) :
// archive l'éventuelle période close, puis pose un mandat actif sans reliquats
// (motifSortie / dateSortie remis à null — un mandat repassé actif gardait
// avant le motif de sa sortie précédente). Les champs annexes d'un mandat
// encore actif (ex. poste, données legacy) sont préservés.
export function ouvrirMandat(d, champ, { debut = "", fin = "" } = {}) {
  const m = d?.[champ];
  return {
    ...archiverMandat(d, champ),
    [champ]: {
      ...(mandatActif(m) ? m : {}),
      debut, fin, statut: "actif", motifSortie: null, dateSortie: null,
    },
  };
}

// Clôt la période courante (sortie de mandat). Le rôle correspondant doit être
// retiré par l'appelant (fermeture des droits) — voir fermerMandatEtRole.
export function fermerMandat(d, champ, { motif = "", date = "" } = {}) {
  const m = d?.[champ];
  if (!m || typeof m !== "object") return {};
  return {
    [champ]: { ...m, statut: "sorti", motifSortie: motif || m.motifSortie || "", dateSortie: date || new Date().toISOString().slice(0, 10) },
  };
}

// Sortie COMPLÈTE : mandat clos + rôle retiré de roles[] (les droits applicatifs
// — accès modules, listes auto, quorum — dérivent de roles[] : les laisser
// ouverts après la sortie était le trou historique « droits jamais fermés »).
// `role` = "CA" | "AG"… ; ignoré s'il n'est pas porté.
export function fermerMandatEtRole(d, champ, role, { motif = "", date = "" } = {}) {
  const roles = Array.isArray(d?.roles) ? d.roles : [];
  return {
    ...fermerMandat(d, champ, { motif, date }),
    ...(role && roles.includes(role) ? { roles: roles.filter((r) => r !== role) } : {}),
  };
}
